import { useCallback, useEffect, useMemo } from 'react';
import {
    useForm, FieldErrors, Control, FieldValues, UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { date, object } from 'yup';
import { checkNullData } from 'utils';
import { getDatesDifference } from 'utils/datetime';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';

export interface CustomRangeValues extends FieldValues {
  startDate: Date | null;
  endDate: Date | null;
}

interface UseCustomRangeReturn {
  control: Control<CustomRangeValues>;
  register: UseFormRegister<CustomRangeValues>;
  formErrors: Record<string, string | undefined>;
  handleSubmit: ReturnType<typeof useForm>['handleSubmit'];
  onChange: (type: keyof CustomRangeValues) => (value: Date | null) => void;
  values: CustomRangeValues;
}

export const useCustomRange = (
    defaultData?: Partial<CustomRangeValues>,
): UseCustomRangeReturn => {
    const { t } = useTranslation();

    const schema = useMemo(
        () => object().shape({
            startDate: date()
                .required(t('validation.mandatoryField'))
                .test(
                    'from-before-to',
                    t('customRange.validation.fromBeforeTo'),
                    function (value) {
                        return value && this?.parent?.endDate
                            ? getDatesDifference(this?.parent?.endDate, value) >= 0
                            : true;
                    },
                ),
            endDate: date()
                .required(t('validation.mandatoryField'))
                .test(
                    'to-after-before',
                    t('customRange.validation.toAfterBefore'),
                    function (value) {
                        return value && this?.parent?.startDate
                            ? getDatesDifference(value, this?.parent?.startDate) >= 0
                            : true;
                    },
                ),
        }),
        [t],
    );

    const resolver = useYupValidationResolver(schema);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
        control,
        watch,
        formState,
        trigger,
    } = useForm<CustomRangeValues>({
        resolver,
        reValidateMode: 'onChange',
        shouldUnregister: false,
    });

    const values = watch();

    // Reset form if defaultData changes
    useEffect(() => {
        if (!checkNullData(defaultData)) {
            reset(defaultData as CustomRangeValues);
        }
    }, [defaultData, reset]);

    // Validation errors in flat format
    const formErrors: Record<string, string | undefined> = Object.keys(
        errors || {},
    ).reduce((acc, key) => {
        const errKey = key as keyof FieldErrors<CustomRangeValues>;

        return {
            ...acc,
            [key]: errors[errKey]?.message as string | undefined,
        };
    }, {});

    // Custom onChange handler
    const onChange = useCallback(
        (type: keyof CustomRangeValues) => (value: Date | null) => {
            setValue(type, value);
            if (formState.isSubmitted) {
                trigger(['startDate', 'endDate']);
            }
        },
        [setValue, trigger, formState.isSubmitted],
    );

    return {
        control,
        register,
        formErrors,
        handleSubmit,
        onChange,
        values,
    };
};
